import dayjs from 'dayjs';

import { LoginState, refreshTokenAPI } from '@/services/logic/login';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from '..';
import { loginOut } from '../utils';
import { LOCAL_STORAGE } from '../enums';
import { message } from 'antd';

// 刷新令牌状态的枚举
enum REFRESH_TOKEN_STATUS {
  NORMAL = 'normal',
  ERROR = 'refresh_token_error',
}

// 设置 access token 到 localStorage
export function setAccessToken(token: string) {
  setLocalStorageItem(LOCAL_STORAGE.ACCESS_TOKEN, token);
}

// 设置 refresh token 到 localStorage
export function setRefreshToken(token: string) {
  setLocalStorageItem(LOCAL_STORAGE.REFRESH_TOKEN, token);
}

// 设置 token 的过期时间到 localStorage
export function setExpiresAt(time: string) {
  setLocalStorageItem(LOCAL_STORAGE.EXPIRES_AT, time);
}

// 设置 refresh token 的过期时间到 localStorage
export function setRefreshTokenExpires(time: string) {
  setLocalStorageItem(LOCAL_STORAGE.REFRESH_TOKEN_EXPIRES_AT, time);
}

// 设置 refresh token 的 lock 到 localStorage
export function setRefreshTokenLock(isLocked: boolean) {
  setLocalStorageItem(LOCAL_STORAGE.TOKEN_REFRESH_LOCK, isLocked);
}

// 获取 access token，从 localStorage 中取值
export function getAccessToken(): string | null {
  return getLocalStorageItem<string>(LOCAL_STORAGE.ACCESS_TOKEN);
}

// 获取 refresh token，从 localStorage 中取值
export function getRefreshToken(): string | null {
  return getLocalStorageItem<string>(LOCAL_STORAGE.REFRESH_TOKEN);
}

// 获取过期时间，从 localStorage 中取值
export function getExpiresAt(): string | null {
  return getLocalStorageItem<string>(LOCAL_STORAGE.EXPIRES_AT);
}

// 获取 refresh token 的过期时间，从 localStorage 中取值
export function getRefreshTokenExpires() {
  return getLocalStorageItem<string>(LOCAL_STORAGE.REFRESH_TOKEN_EXPIRES_AT);
}

// 获取 refresh token 的 lock，从 localStorage 中取值
export function getRefreshTokenLock() {
  return getLocalStorageItem(LOCAL_STORAGE.TOKEN_REFRESH_LOCK);
}

// 清除所有 token 信息（退出登录时调用）
export function clearAuthToken() {
  removeLocalStorageItem(LOCAL_STORAGE.ACCESS_TOKEN);
  removeLocalStorageItem(LOCAL_STORAGE.REFRESH_TOKEN);
  removeLocalStorageItem(LOCAL_STORAGE.EXPIRES_AT);
  removeLocalStorageItem(LOCAL_STORAGE.REFRESH_TOKEN_EXPIRES_AT);
  removeLocalStorageItem(LOCAL_STORAGE.TOKEN_REFRESH_LOCK);
}

// 登录后的状态接口
export interface TokenState {
  access_token: string;
  refresh_token: string;
  access_token_expires: string;
  refresh_token_expires: string;
  is_updating?: boolean;
}

// 模块内部状态
let updateTimer: NodeJS.Timeout | null = null;
const UPDATE_THRESHOLD_MINUTES = 20;

/**
 * 初始化 token 并保存到本地
 */
export function initToken(tokenData: LoginState): void {
  const now = dayjs();
  const {
    access_token,
    token_type = 'Bearer',
    refresh_token,
    expires_in,
    refresh_token_expires_in = 0,
  } = tokenData || {};
  const tokenState: TokenState = {
    access_token: `${access_token}`,
    refresh_token: refresh_token,
    access_token_expires: now.add(expires_in, 'second').toISOString(),
    refresh_token_expires: now.add(refresh_token_expires_in, 'second').toISOString(),
    is_updating: false,
  };

  saveToLocalCache(tokenState);
}

/**
 * 获取当前 access_token
 */
export async function getToken(retry = 1): Promise<string | null> {
  let tokenState = getFromLocalCache();
  if (!tokenState) return null;

  if (dayjs().isAfter(dayjs(tokenState.access_token_expires))) {
    // 如果access token过期，则需要更新access token
    const result = await checkAndUpdateToken();

    // 如果更新失败，可能是refresh token过期或其他错误，就只重试 3 次
    const isRefreshErrorRetryGt3 = retry > 4 && result === REFRESH_TOKEN_STATUS.ERROR;
    // 如果重试超过 15 次，直接返回登录页
    const isRefreshRetryGt15 = retry > 15;
    if (isRefreshErrorRetryGt3 || isRefreshRetryGt15) {
      tokenState = getFromLocalCache();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 每次获取 token 暂停 1 秒
      return await getToken(retry + 1); // 递归调用获取token
    }
  }

  return tokenState!.access_token;
}

/**
 * 清除 token 数据
 */
export function clearToken(): void {
  stopUpdateTimer();
  clearAuthToken();
}

/**
 * 判断 refresh_token 是否过期
 */
export function isRefreshTokenExpired(): boolean {
  try {
    const tokenState = getFromLocalCache();
    if (!tokenState) return true;
    return dayjs().isAfter(dayjs(tokenState.refresh_token_expires));
  } catch {
    return true;
  }
}

/**
 * 判断 access_token 是否即将过期
 */
export function isAccessTokenExpiringSoon(): boolean {
  const tokenState = getFromLocalCache();
  if (!tokenState) return false;

  const expiresAt = dayjs(tokenState.access_token_expires);
  const threshold = dayjs().add(UPDATE_THRESHOLD_MINUTES, 'minute');
  return expiresAt.isBefore(threshold);
}

/**
 * 更新 access_token
 */
export async function updateAccessToken(): Promise<REFRESH_TOKEN_STATUS> {
  const tokenState = getFromLocalCache();

  // 如果没有token，或者 token 过期，则清除 token 并退出登录
  if (!tokenState || isRefreshTokenExpired()) {
    clearToken();
    await loginOut(); // 退出登录
    return REFRESH_TOKEN_STATUS.NORMAL;
  }

  if (tokenState.is_updating) return REFRESH_TOKEN_STATUS.NORMAL;

  try {
    setUpdating(true);
    const data = await refreshTokenAPI({
      refresh_token: tokenState.refresh_token,
    });

    if (data && data.access_token) {
      const now = dayjs();
      const {
        access_token,
        token_type = 'Bearer',
        refresh_token,
        expires_in,
        refresh_token_expires_in = 0,
      } = data || {};
      const newTokenState: TokenState = {
        access_token: `${access_token}`,
        refresh_token: refresh_token,
        access_token_expires: now.add(expires_in, 'second').toISOString(),
        refresh_token_expires: now.add(refresh_token_expires_in, 'second').toISOString(),
        is_updating: false,
      };
      saveToLocalCache(newTokenState);
      return REFRESH_TOKEN_STATUS.NORMAL;
    }
    return REFRESH_TOKEN_STATUS.ERROR;
  } catch (error) {
    message.error('刷新 token 失败，请重新登录');
    return REFRESH_TOKEN_STATUS.ERROR;
  } finally {
    setUpdating(false);
  }
}

/**
 * 启动定时器每分钟检查一次是否需要更新 token
 */
export function startUpdateTimer(): void {
  stopUpdateTimer();
  // NOTE: 由于后端没有加锁，所以这里的定时任务会在 多 tab 标签页是出现并发问题。
  // 所以暂时给它取消掉。
  // updateTimer = setInterval(() => {
  //   checkAndUpdateToken();
  // }, 60 * 1000);
}

/**
 * 停止定时器
 */
function stopUpdateTimer(): void {
  if (updateTimer) {
    clearInterval(updateTimer);
    updateTimer = null;
  }
}

/**
 * 检查并更新 token
 */
async function checkAndUpdateToken(): Promise<REFRESH_TOKEN_STATUS> {
  if (isAccessTokenExpiringSoon()) {
    return await updateAccessToken();
  }

  return REFRESH_TOKEN_STATUS.NORMAL;
}

/**
 * 从 缓存中 获取 token 状态
 */
function getFromLocalCache(): TokenState | null {
  const access_token = getAccessToken();
  const refresh_token = getRefreshToken();
  const access_token_expires = getExpiresAt();
  const refresh_token_expires = getRefreshTokenExpires();
  const is_updating = getRefreshTokenLock();

  if (!access_token || !refresh_token || !access_token_expires || !refresh_token_expires) {
    return null;
  }

  return {
    access_token,
    refresh_token,
    access_token_expires,
    refresh_token_expires,
    is_updating: !!is_updating,
  };
}

/**
 * 保存 token 状态到 缓存中
 */
function saveToLocalCache(tokenState: TokenState): void {
  const { access_token, refresh_token, access_token_expires, refresh_token_expires, is_updating } =
    tokenState;

  setAccessToken(access_token); // 存储 accessToken
  setRefreshToken(refresh_token); // 存储 refreshToken
  setExpiresAt(access_token_expires); // 存储过期时间
  setRefreshTokenExpires(refresh_token_expires); // 存储 refresh token 过期时间
  setRefreshTokenLock(is_updating as boolean); // 存储 refresh token 的锁状态
}

/**
 * 设置 token 更新状态
 */
function setUpdating(isUpdating: boolean): void {
  const tokenState = getFromLocalCache();
  if (tokenState) {
    tokenState.is_updating = isUpdating;
    saveToLocalCache(tokenState);
  }
}
